/**
 * Token Cache Manager
 * Optimizes token acquisition by caching tokens and checking expiration
 * Reduces unnecessary calls to acquireTokenSilent()
 */

import { PublicClientApplication } from '@azure/msal-browser';
import { getAuthScopes } from '../config/msal.config';
import { logAuthEvent } from './authTelemetry.utils';
import { acquireTokenSilentDeduplicated } from './tokenAcquisition.utils';

interface CachedToken {
  token: string;
  expiresAt: number;
  scopes: string[];
}

/**
 * Token cache with expiration management
 */
class TokenCache {
  private cache: CachedToken | null = null;
  private readonly bufferTimeMs = 5 * 60 * 1000; // 5 minutes buffer before expiration

  /**
   * Get valid token from cache or acquire new one
   */
  async getValidToken(msalInstance: PublicClientApplication): Promise<string | null> {
    // Check if cached token is still valid
    if (this.cache && this.isTokenValid(this.cache)) {
      if (import.meta.env.DEV) {
        console.log('[Token Cache] Using cached token');
      }
      return this.cache.token;
    }

    // Cache miss or expired - acquire new token
    try {
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length === 0) {
        this.clearCache();
        return null;
      }

      // Set active account if accounts exist but no active account is set
      if (!msalInstance.getActiveAccount() && accounts.length > 0 && accounts[0]) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      const account = msalInstance.getActiveAccount();
      if (!account) {
        throw new Error("Active account not set for silent authentication");
      }
      
      const scopes = getAuthScopes();

      if (import.meta.env.DEV) {
        console.log('[Token Cache] Acquiring new token from MSAL');
      }

      if (account) {
        logAuthEvent('silent_token_acquisition', {
          userId: account.homeAccountId,
          email: account.username,
        });
      }

      // Use centralized token acquisition with deduplication
      const response = await acquireTokenSilentDeduplicated(
        msalInstance,
        account,
        scopes
      );

      // Cache the new token
      const expiresAt = response.expiresOn ? response.expiresOn.getTime() : Date.now() + 3600000; // Default 1 hour
      
      this.cache = {
        token: response.accessToken,
        expiresAt: expiresAt,
        scopes: scopes,
      };

      return response.accessToken;
    } catch (error) {
      console.error('[Token Cache] Failed to acquire token:', error);
      this.clearCache();
      
      // Log error
      logAuthEvent('token_refresh_failure', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      // Check if interaction is required
      if (error instanceof Error) {
        const errorMessage = error.message.toLowerCase();
        if (errorMessage.includes('interaction_required') || 
            errorMessage.includes('consent_required') ||
            errorMessage.includes('login_required')) {
          logAuthEvent('interaction_required');
        }
      }

      return null;
    }
  }

  /**
   * Check if cached token is valid (not expired with buffer)
   */
  private isTokenValid(cachedToken: CachedToken): boolean {
    const now = Date.now();
    const expiresWithBuffer = cachedToken.expiresAt - this.bufferTimeMs;
    return now < expiresWithBuffer;
  }

  /**
   * Clear cached token
   */
  clearCache(): void {
    this.cache = null;
    if (import.meta.env.DEV) {
      console.log('[Token Cache] Cache cleared');
    }
  }

  /**
   * Get cached token (without validation)
   */
  getCachedToken(): CachedToken | null {
    return this.cache;
  }

  /**
   * Force refresh token (ignores cache)
   */
  async forceRefresh(msalInstance: PublicClientApplication): Promise<string | null> {
    this.clearCache();
    return this.getValidToken(msalInstance);
  }

  /**
   * Get time until token expires (in milliseconds)
   */
  getTimeUntilExpiration(): number | null {
    if (!this.cache) {
      return null;
    }
    return Math.max(0, this.cache.expiresAt - Date.now());
  }

  /**
   * Check if token will expire soon (within buffer time)
   */
  willExpireSoon(): boolean {
    if (!this.cache) {
      return true;
    }
    const timeUntilExpiration = this.getTimeUntilExpiration();
    return timeUntilExpiration !== null && timeUntilExpiration < this.bufferTimeMs;
  }
}

// Singleton instance
const tokenCache = new TokenCache();

/**
 * Get valid access token (uses cache when possible)
 */
export async function getValidAccessToken(msalInstance: PublicClientApplication): Promise<string | null> {
  return tokenCache.getValidToken(msalInstance);
}

/**
 * Clear token cache
 */
export function clearTokenCache(): void {
  tokenCache.clearCache();
}

/**
 * Force token refresh (ignores cache)
 */
export async function forceTokenRefresh(msalInstance: PublicClientApplication): Promise<string | null> {
  return tokenCache.forceRefresh(msalInstance);
}

/**
 * Get time until current token expires
 */
export function getTimeUntilTokenExpiration(): number | null {
  return tokenCache.getTimeUntilExpiration();
}

/**
 * Check if token will expire soon
 */
export function willTokenExpireSoon(): boolean {
  return tokenCache.willExpireSoon();
}

/**
 * Get cached token info (for debugging)
 */
export function getCachedTokenInfo(): CachedToken | null {
  return tokenCache.getCachedToken();
}

// Export singleton for advanced usage
export { tokenCache };
